import { keyframes } from "@emotion/react";
import { Box, Container, Link, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import type { JSX } from "react";
import { Link as RouterLink } from "react-router-dom";

import { DotGlobeCanvas } from "../../components/globe/DotGlobeCanvas.js";
import { XOP_CONTACT } from "../../lib/company.js";
import { Nameplate } from "../login/Nameplate.js";
import { CardGrid, CtaBand, CtaButton, Section, SectionHeading, StepList } from "./blocks.js";
import { BENEFITS, HERO, PAGE_META, PORTAL_FEATURES, PUBLIC_ROUTES, STATS, STEPS } from "./content.js";
import { usePageMeta } from "./usePageMeta.js";

/* The sign-in page's globe entrance: scale 1.16 → 1 while fading
 * up, so the planet settles into the page. Duplicated from LoginPage rather
 * than exported from it — a page module is not a place to import a keyframe
 * from, and the two pages are allowed to drift. */
const globeIntro = keyframes`
  0%   { opacity: 0; transform: scale(1.16); }
  60%  { opacity: 1; }
  100% { opacity: 1; transform: scale(1); }
`;

/**
 * `/` for anyone who is not signed in (spec §5). The one page on the site
 * whose job is to make the case; the program page carries the detail.
 *
 * The hero re-uses the sign-in nameplate treatment for the headline — the
 * same staggered words, the same tight tracking — so the front door and the
 * sign-in read as one site rather than a marketing template stapled to an
 * app. Everything below the hero renders from `content.ts`.
 */
export function LandingPage(): JSX.Element {
  usePageMeta(PAGE_META.home);
  const theme = useTheme();
  const showGlobe = useMediaQuery(theme.breakpoints.up("md"), { noSsr: true });

  return (
    <>
      {/* ── Hero */}
      <Box component="section" id="hero" aria-labelledby="hero-heading" sx={{ pt: { xs: 8, md: 12 }, pb: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          {/* `minmax(0, 1fr)` at `xs`, not `1fr`: a bare `1fr` is
              `minmax(auto, 1fr)`, so the track floors at its content's
              min-content width — and this grid's content includes a
              `<canvas>` whose `width` ATTRIBUTE `DotGlobeCanvas` sets to the
              CSS width × devicePixelRatio. On a 2× phone that intrinsic size
              fed back as a ~400px floor and pushed the hero (copy column
              included) past a 375px viewport, where the layout's `overflowX:
              hidden` silently clipped it. */}
          <Box sx={{ display: "grid", gap: { xs: 5, md: 6 }, gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 7fr) minmax(0, 5fr)" }, alignItems: "center" }}>
            <Box>
              <Box sx={{ "& h1": { textAlign: "left", mb: 3, fontSize: "clamp(2.4rem, 6vw, 4.4rem)" } }}>
                <Nameplate id="hero-heading" text={HERO.headline} />
              </Box>
              <Typography sx={{ maxWidth: 620, fontSize: { xs: 17, md: 19 }, lineHeight: 1.6, color: "text.secondary" }}>{HERO.lede}</Typography>
              <Box sx={{ mt: 4, display: "flex", gap: 2, flexWrap: "wrap" }}>
                <CtaButton cta={HERO.primary} />
                <CtaButton cta={HERO.secondary} variant="secondary" />
              </Box>
            </Box>
            {/* The globe is a desktop-and-tablet ornament only (product
                owner, 2026-09-08: "on the mobile view we can remove this
                globe thing"). It is NOT mounted below `md` rather than
                hidden with `display: none`, because `DotGlobeCanvas` runs a
                requestAnimationFrame loop for as long as it is in the tree —
                a phone would burn battery animating a planet nobody sees.
                `noSsr` for the same reason as `SiteNav`: this SPA never
                server-renders, so the first paint can read the viewport. */}
            {showGlobe ? (
              <Box aria-hidden sx={{ position: "relative", aspectRatio: "1 / 1", maxWidth: "min(100%, 520px)", width: "100%", minWidth: 0, mx: "auto", animation: `${globeIntro} 1500ms cubic-bezier(0.22, 1, 0.36, 1) both`, "@media (prefers-reduced-motion: reduce)": { animation: "none" } }}>
                <DotGlobeCanvas />
              </Box>
            ) : null}
          </Box>
        </Container>
      </Box>

      {/* ── Proof strip */}
      <Box component="section" aria-label="XOP Networks at a glance" sx={{ py: { xs: 3, md: 4 }, borderTop: 1, borderBottom: 1, borderColor: "divider" }}>
        <Container maxWidth="lg">
          <Box component="dl" sx={{ m: 0, display: "grid", gap: 3, gridTemplateColumns: { xs: "repeat(2, minmax(0,1fr))", md: "repeat(4, minmax(0,1fr))" } }}>
            {STATS.map((s) => (
              <Box key={s.label}>
                <Typography component="dt" sx={{ fontSize: { xs: "1.8rem", md: "2.4rem" }, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1 }}>{s.figure}</Typography>
                <Typography component="dd" sx={{ m: 0, mt: 0.75, color: "text.secondary", fontSize: 14 }}>{s.label}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ── Benefits */}
      <Section id="benefits">
        <SectionHeading id="benefits" title="Why partner with XOP Networks" lede="Four things every active partner gets, taken from the program as XOP Networks publishes it." />
        <CardGrid columns={4} items={BENEFITS.map((b) => ({ title: b.title, body: b.summary }))} />
      </Section>

      {/* ── How it works */}
      <Section id="how" tone="soft">
        <SectionHeading id="how" title="How it works" lede="From application to your first quote in four steps — the first three are on us to keep short." />
        <StepList aria-label="How it works" steps={STEPS.map((s) => ({ title: s.title, body: s.summary }))} />
        <Typography sx={{ mt: 4 }}>
          <Link component={RouterLink} to={PUBLIC_ROUTES.program} sx={{ fontWeight: 600, color: "accent.500" }}>See the full program →</Link>
        </Typography>
      </Section>

      {/* ── Portal features */}
      <Section id="portal">
        <SectionHeading id="portal" title="What you get in the portal" lede="Everything behind the sign-in exists today — this is what opens the moment you are activated." />
        <CardGrid columns={3} items={PORTAL_FEATURES.map((f) => ({ title: f.title, body: f.summary }))} />
      </Section>

      <CtaBand
        id="cta"
        title="Ready to grow with XOP Networks?"
        body="Apply in five minutes. A person at XOP Networks reads every application."
        primary={{ label: "Apply now", href: PUBLIC_ROUTES.register }}
        secondary={{ label: "Talk to sales", href: `mailto:${XOP_CONTACT.sales}` }}
        secondaryExternal
      />
    </>
  );
}
